/**
 * Shared location attribute builders for Medical Necessity analytics.
 * @module medicalNecessityAnalytics/locationAttrs
 */
import { NCCNEvaluationRecord } from "../../../data-model/nccnEvaluation";
import { EvaluationItem } from "../../../data-model/nccnGuidelines";
import { getGuidelineReviewVersion } from "./guidelineReviewVersion";

/** Shared guideline + criterion position attributes. */
export interface MnLocationAttrs {
  [key: string]: string | number | undefined;
  guideline_id: string;
  guideline_index: number;
  guideline_review_version: number;
  criterion_id?: string;
  criterion_index?: number;
  criterion_visit_number?: number;
}

/**
 * Builds location attrs for a guideline (and optional criterion).
 *
 * @param analysisId - Analysis ID
 * @param guideline - Guideline record
 * @param guidelineIndex - One-based display index
 * @param item - Optional criterion
 * @param criterionIndex - Optional one-based criterion index
 * @param visitNumber - Optional criterion visit number
 * @returns Location attribute bag
 */
export const buildLocationAttrs = (
  analysisId: string,
  guideline: NCCNEvaluationRecord,
  guidelineIndex: number,
  item?: EvaluationItem,
  criterionIndex?: number,
  visitNumber?: number,
): MnLocationAttrs => {
  const guidelineId = guideline.guideline_id ?? "";
  const attrs: MnLocationAttrs = {
    guideline_id: guidelineId,
    guideline_index: guidelineIndex,
    guideline_review_version: getGuidelineReviewVersion(
      analysisId,
      guidelineId,
    ),
  };
  if (item && criterionIndex != null) {
    attrs.criterion_id = item.id;
    attrs.criterion_index = criterionIndex;
    if (visitNumber != null) {
      attrs.criterion_visit_number = visitNumber;
    }
  }
  return attrs;
};
